/**
 * "Dữ liệu của tôi" (TT 83 C7-b, NĐ 13/2023): xem tóm tắt + tải bản sao JSON dữ liệu hệ
 * thống đang lưu, gửi yêu cầu (sửa/xoá/rút đồng ý…), theo dõi yêu cầu.
 */
import { useState } from "react";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  KIND_LABEL,
  STATUS_LABEL,
  type DataRequest,
  type DataRequestKind,
  createMyRequest,
  getMyData,
  listMyRequests,
} from "@/features/privacy/api";
import { formatDateTime } from "@/lib/formatDate";

const { Title, Paragraph } = Typography;

export function MyData() {
  const queryClient = useQueryClient();
  const [form] = Form.useForm<{ kind: DataRequestKind; detail: string }>();
  const [downloading, setDownloading] = useState(false);
  const data = useQuery({ queryKey: ["my-data"], queryFn: getMyData });
  const requests = useQuery({ queryKey: ["my-data-requests"], queryFn: listMyRequests });
  const create = useMutation({
    mutationFn: (v: { kind: DataRequestKind; detail: string }) =>
      createMyRequest(v.kind, v.detail ?? ""),
    onSuccess: () => {
      message.success("Đã gửi yêu cầu tới nhà trường");
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ["my-data-requests"] });
    },
    onError: () => message.error("Không gửi được yêu cầu"),
  });

  const download = async () => {
    setDownloading(true);
    try {
      const fresh = await getMyData();
      const url = URL.createObjectURL(
        new Blob([JSON.stringify(fresh, null, 2)], { type: "application/json" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = "du-lieu-cua-toi.json";
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const account = data.data?.account ?? {};
  const profile = data.data?.learner_profile;
  return (
    <div style={{ maxWidth: 860, margin: "0 auto" }}>
      <Title level={3}>Dữ liệu của tôi</Title>
      <Paragraph type="secondary">
        Theo Nghị định 13/2023, bạn có quyền biết, nhận bản sao, yêu cầu sửa, xoá dữ liệu cá nhân và
        rút đồng ý. Hồ sơ học vụ (mã sinh viên, điểm, nhật ký học tập) trường có nghĩa vụ lưu theo
        quy định nên không xoá được.
      </Paragraph>
      <Card
        title="Dữ liệu hệ thống đang lưu"
        loading={data.isPending}
        extra={
          <Button icon={<DownloadOutlined />} loading={downloading} onClick={download}>
            Tải bản sao (JSON)
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        <Descriptions column={1} size="small" data-testid="my-data-summary">
          <Descriptions.Item label="Email">{String(account.email ?? "")}</Descriptions.Item>
          <Descriptions.Item label="Mã sinh viên">
            {String(account.student_id ?? "") || "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Số định danh">
            {String(profile?.national_id_masked ?? "") || "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Môn đã ghi danh">
            {data.data?.enrollments.length ?? 0}
          </Descriptions.Item>
          <Descriptions.Item label="Lượt làm bài">
            {data.data?.attempts.length ?? 0}
          </Descriptions.Item>
          <Descriptions.Item label="Hoạt động học tập đã ghi nhận">
            {data.data?.learning_events.total ?? 0}
          </Descriptions.Item>
        </Descriptions>
      </Card>
      <Card title="Gửi yêu cầu" style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical" onFinish={(v) => create.mutate(v)}>
          <Form.Item
            name="kind"
            label="Loại yêu cầu"
            rules={[{ required: true, message: "Chọn loại yêu cầu" }]}
          >
            <Select
              options={(Object.keys(KIND_LABEL) as DataRequestKind[]).map((k) => ({
                value: k,
                label: KIND_LABEL[k],
              }))}
            />
          </Form.Item>
          <Form.Item name="detail" label="Nội dung">
            <Input.TextArea rows={3} maxLength={2000} />
          </Form.Item>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Rút đồng ý xác thực khuôn mặt: dùng trang Xác thực danh tính (xoá ảnh ngay)."
          />
          <Button type="primary" htmlType="submit" loading={create.isPending}>
            Gửi yêu cầu
          </Button>
        </Form>
      </Card>
      <Card title="Yêu cầu đã gửi">
        <Table<DataRequest>
          rowKey="id"
          size="small"
          loading={requests.isPending}
          dataSource={requests.data?.results ?? []}
          pagination={false}
          columns={[
            { title: "Lúc", dataIndex: "created_at", render: (v: string) => formatDateTime(v) },
            { title: "Loại", dataIndex: "kind", render: (k: DataRequestKind) => KIND_LABEL[k] },
            {
              title: "Trạng thái",
              dataIndex: "status",
              render: (s: keyof typeof STATUS_LABEL) => <Tag>{STATUS_LABEL[s]}</Tag>,
            },
            { title: "Phản hồi", dataIndex: "response" },
          ]}
        />
      </Card>
      <Space />
    </div>
  );
}
